// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { convertLinkToPageType } from '@way/theme-host/shared/utils/preview-navigation.util';
import { resolveHeaderCtaLink } from '../header-cta.util';

export interface NavigationItem {
  label: string;
  link: string;
  active?: boolean;
  pageType?: string; // Optional pageType for click handler
}

export interface Logo {
  image?: string;
  alt?: string;
  text?: string;
  link?: string;
}

export interface CTAButton {
  text?: string;
  label?: string;
  link?: string;
  buttonNavigation?: string;
  backgroundColor?: string;
  textColor?: string;
}

/**
 * ModernPageHeaderComponent - A reusable header component for the modern theme that displays a logo, navigation, and a CTA button; handles user interactions and maps links to internal page types for navigation events.
 * @component
 * @selector app-modern-page-header
 * @example
 *   <app-modern-page-header
 *     [logo]="logo"
 *     [navigation]="navigationItems"
 *     [ctaButton]="cta"
 *     [visible]="true"
 *     [clickable]="true"
 *     [selected]="false"
 *     [headingColor]="'#1E3A8A'"
 *     [headingFont]="'Inter'"
 *     [backgroundColor]="'#ffffff'"
 *     (headerClick)="onHeaderClicked()"
 *     (navigationClick)="onNavigate($event)">
 *   </app-modern-page-header>
 * @input {Logo | null} logo - Logo object to display in the header (may be null if no logo).
 * @input {NavigationItem[]} navigation - Array of navigation items used to build the header navigation.
 * @input {CTAButton | null} ctaButton - Optional CTA button configuration (link, label, etc.).
 * @input {boolean} visible - Controls whether the header is visible.
 * @input {boolean} clickable - When true, header clicks will emit headerClick events.
 * @input {boolean} selected - Visual state flag indicating if the header is selected.
 * @input {string | null} headingColor - Optional branding color for the heading; default provided if null.
 * @input {string | null} headingFont - Optional branding font for the heading; default provided if null.
 * @input {string | null} backgroundColor - Optional background color for the header.
 * @output {EventEmitter<void>} headerClick - Emitted when the header is clicked (only if clickable is true).
 * @output {EventEmitter<string>} navigationClick - Emitted with a normalized pageType string when a navigation item or CTA is activated.
 *
 * Key methods:
 * - onHeaderClick(): Emits headerClick when clickable; simple guard for header interactions.
 * - onNavigationItemClick(item, event): Prevents default link behavior, stops propagation, guards against duplicate clicks, resolves a pageType from item.pageType or item.link (using normalization, common pattern mapping, query parameter extraction, and path matching), emits navigationClick(pageType), closes mobile menu, and resets navigation guard after a short delay.
 * - onCTAButtonClick(event): Prevents default behavior, normalizes the CTA link and maps common patterns to pageType (express-wash-packages, express-auto-details, fleet-management, coupons, etc.), attempts to match the CTA link to a navigation item for an authoritative pageType, emits navigationClick(pageType), and closes the mobile menu.
 * - getHeadingColor(): Returns the configured headingColor or a default '#1E3A8A'.
 * - getHeadingFont(): Returns the configured headingFont or a default 'Inter'.
 *
 * Important internal state and behavior:
 * - isMobileMenuOpen: boolean flag controlling mobile menu visibility; automatically closed on navigation/CTA activation.
 * - isNavigating: private boolean used to prevent duplicate navigation clicks; reset after a short timeout (300ms).
 * - Link-to-pageType normalization: robust handling of hashes, leading/trailing slashes, hyphen variations, common aliases, query parameters (pageType), and preview path patterns; falls back to 'home' when no pageType can be determined.
 *
 * Lifecycle hooks:
 * - This component does not declare Angular lifecycle hooks explicitly in the implementation shown; it relies on inputs and event emitters for external interaction and internal state management for navigation throttling and mobile menu control.
 */
@Component({
  selector: 'app-modern-page-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./page-header.component.scss'],
})
export class ModernPageHeaderComponent {
  @Input() logo: Logo | null = null;
  @Input() navigation: NavigationItem[] = [];
  @Input() ctaButton: CTAButton | null = null;
  @Input() visible = true;
  @Input() clickable = false;
  @Input() selected = false;
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() backgroundColor: string | null = null; // Background color for header
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() headerClick = new EventEmitter<void>();
  @Output() navigationClick = new EventEmitter<string>();

  // Mobile menu state
  mobileMenuOpen = false;

  // Prevent duplicate clicks
  private isNavigating = false;

  toggleMobileMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  onHeaderClick(): void {
    if (this.clickable) {
      this.headerClick.emit();
    }
  }

  getNavigationItems(): NavigationItem[] {
    return this.navigation?.length ? this.navigation : ModernPageHeaderComponent.defaultNavigation;
  }

  private static readonly defaultNavigation: NavigationItem[] = [
    { label: 'Home', link: '#home' },
    { label: 'Express Washes', link: '#express-wash-packages' },
    { label: 'Express Details', link: '#express-auto-details' },
    { label: 'Fleets', link: '#fleet-management' },
    { label: 'Coupons', link: '#coupons' },
  ];

  /**
   * Handle a navigation item click: determine the target pageType from the item/link, emit navigationClick, close mobile menu, and guard against duplicate navigation.
   * @example
   * nNavigationItemClick(sampleItem, sampleEvent)
   * undefined
   * @param {{NavigationItem}} {{item}} - The navigation item that was clicked.
   * @param {{Event}} {{event}} - The DOM event from the click action.
   * @returns {{void}} Return nothing.
   **/
  onNavigationItemClick(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Prevent duplicate clicks
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    // Use pageType if available (preferred method)
    let pageType = item.pageType;

    if (!pageType && item.link) {
      // Remove hash, leading/trailing slashes and normalize
      const normalizedLink = item.link
        .replace(/^#+|\/+$/g, '')
        .replace(/^\/+/, '')
        .toLowerCase();

      // Map common link patterns to pageType
      if (normalizedLink === '' || normalizedLink === 'home' || normalizedLink === '/') {
        pageType = 'home';
      } else if (
        normalizedLink === 'expresswashes' ||
        normalizedLink === 'express-washes' ||
        normalizedLink.includes('express-wash')
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink === 'express-details' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto')
      ) {
        pageType = 'express-auto-details';
      } else if (
        normalizedLink === 'fleets' ||
        normalizedLink === 'fleet' ||
        normalizedLink.includes('fleet')
      ) {
        pageType = 'fleet-management';
      } else if (
        normalizedLink === 'coupons' ||
        normalizedLink === 'coupon' ||
        normalizedLink.includes('coupon')
      ) {
        pageType = 'coupons';
      } else {
        // Try to extract pageType from query parameters: /preview?websiteId=...&pageType=...
        const urlParams = new URLSearchParams(item.link.split('?')[1] || '');
        pageType = urlParams.get('pageType') || undefined;

        // If still not found, try to extract from path: /website-preview/{templateId}/{pageType}
        if (!pageType) {
          const match = item.link.match(/\/website-preview\/[^/]+(?:\/(.+))?$/);
          if (match) {
            pageType = match[1] || 'home';
          } else {
            // Fallback: use the normalized link as pageType
            pageType = normalizedLink || 'home';
          }
        }
      }
    }

    // Default to home if still no pageType found
    if (!pageType) {
      pageType = 'home';
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when navigation item is clicked
    this.mobileMenuOpen = false;

    // Reset navigation flag after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 300);
  }

  /**
   * Handle CTA button click: prevent default behavior, determine destination page type from the CTA link, emit navigation event and close mobile menu.
   * @example
   * nCTAButtonClick(event)
   * undefined
   * @param {Event} event - Click event from the CTA button; this function prevents default and stops propagation.
   * @returns {void} No return value.
   */
  onCTAButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const link = resolveHeaderCtaLink(this.ctaButton);
    const pageType = convertLinkToPageType(link, []);
    this.navigationClick.emit(pageType);
    this.mobileMenuOpen = false;
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#1E3A8A';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
